"use client";

import { useState } from "react";
import { Check, Minus } from "lucide-react";
import StepHeading from "@/app/components/molecules/StepHeading";
import FormCard from "@/app/components/atoms/FormCard";
import ConfirmedEmailCard from "@/app/components/atoms/ConfirmedEmailCard";
import Button from "@/app/components/atoms/Button";
import InfoBanner from "@/app/components/atoms/InfoBanner";
import PasswordInput from "@/app/components/atoms/PasswordInput";
import { getPasswordRules } from "@/lib/auth/password";

const AMBIGUOUS_ACCOUNT_ERROR =
  "We were unable to uniquely identify your account. Please contact tickets@groundsforsculpture.org to update your information.";

interface RegisterCreatePasswordStepProps {
  /** Email confirmed in step 1. */
  email: string;
  /** Go back to the email step to edit email. */
  onEditEmail: () => void;
  /** Called with the chosen password when the user continues. */
  onContinue: (password: string) => void;
  /** Whether account creation is in progress. */
  loading?: boolean;
  /** Error from account creation. */
  error?: string | null;
}

/**
 * @component RegisterCreatePasswordStep
 * @description Password creation step of the registration flow. Shows the
 * confirmed email via ConfirmedEmailCard, then collects and validates a new password.
 * @param email - Confirmed email.
 * @param onEditEmail - Returns to email entry step.
 * @param onContinue - Advances to confirming step with the password.
 * @param loading - Shows loading state on the submit button.
 * @param error - Form-level error message.
 * @returns The create-password form.
 */
export default function RegisterCreatePasswordStep({
  email,
  onEditEmail,
  onContinue,
  loading,
  error,
}: RegisterCreatePasswordStepProps) {
  const showAmbiguousAccountInfo = error === AMBIGUOUS_ACCOUNT_ERROR;
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const passwordHasSpace = /\s/.test(password);
  const confirmHasSpace = /\s/.test(confirm);
  const passwordsMatch = password === confirm;
  const mismatch = confirm.length > 0 && !passwordsMatch;

  const rules = getPasswordRules(password);
  const allRulesMet = rules.every((r) => r.met);
  const canContinue =
    password.trim() &&
    confirm.trim() &&
    passwordsMatch &&
    allRulesMet &&
    !passwordHasSpace &&
    !confirmHasSpace;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canContinue) return;
    onContinue(password);
  };

  return (
    <div className="flex flex-col gap-8">
      <StepHeading id="register-password-header" title="Create a password" />

      <div className="w-full max-w-md m-auto flex flex-col gap-6">
        <ConfirmedEmailCard email={email} onEdit={onEditEmail} />

        <FormCard>
          <form
            id="register-password-form"
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <p className="text-title text-charcoal">Create a password</p>

            <PasswordInput
              id="reg-password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="new-password"
              required
              invalid={passwordHasSpace}
              error={
                passwordHasSpace ? "Password cannot contain spaces." : undefined
              }
              errorId="reg-password-error"
            />

            <PasswordInput
              id="reg-confirm"
              label="Confirm Password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm Password"
              autoComplete="new-password"
              required
              invalid={confirmHasSpace || mismatch}
              error={
                confirmHasSpace
                  ? "Password cannot contain spaces."
                  : mismatch
                    ? "Passwords do not match."
                    : undefined
              }
              errorId="reg-confirm-error"
            />

            <div className="pt-1">
              <p className="text-label text-muted-text mb-2">
                Password requirements
              </p>
              <ul className="text-body-base space-y-1.5">
                {rules.map((rule) => (
                  <li
                    key={rule.label}
                    className={`flex items-center gap-2 ${rule.met ? "text-charcoal" : "text-muted-text"}`}
                  >
                    {rule.met ? (
                      <Check
                        className="w-4 h-4 shrink-0 text-accent-green"
                        aria-hidden
                      />
                    ) : (
                      <Minus className="w-4 h-4 shrink-0" aria-hidden />
                    )}
                    {rule.label}
                  </li>
                ))}
              </ul>
            </div>

            {showAmbiguousAccountInfo && (
              <InfoBanner>{AMBIGUOUS_ACCOUNT_ERROR}</InfoBanner>
            )}
            {error && !showAmbiguousAccountInfo && (
              <p
                role="alert"
                className="text-body-base text-destructive text-center"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              size="full"
              disabled={!canContinue}
              loading={loading}
            >
              Continue
            </Button>
          </form>
        </FormCard>
      </div>
    </div>
  );
}
